import MojitoCore from 'mojito/core';
import { createSlice } from '@reduxjs/toolkit';
import { isLoggedIn } from 'services/authentication/selectors.js';
import { selectOddsFormat } from 'services/user-settings/selectors.js';
import { actions as bootstrapActions } from 'services/bootstrap/slice.js';
import { actions as authenticationActions } from 'services/authentication/slice.js';
import dataRetriever from './settled-bets-data-retriever.js';
import { selectDataHash, selectIsPending, selectIsRequested } from './settled-bets-selectors.js';
import ServicesTypes from 'services/common/types.js';
import BetsTypes from 'services/bets/types.js';

const { CONTENT_STATE } = ServicesTypes;
const reduxInstance = MojitoCore.Services.redux;
const { SETTLED_BETS_STORE_KEY: STORE_KEY } = BetsTypes;

const betsInitState = {
    bets: [],
    loadingStatus: CONTENT_STATE.UNKNOWN,
    dataHash: undefined,
};

/**
 * Defines the state of the settled bets store.
 *
 * @typedef SettledBetsState
 * @type {object}
 * @property {Array} bets - Bets.
 * @property {Mojito.Services.Common.types.CONTENT_STATE} loadingStatus - Loading state for settled bets.
 * @property {boolean} isRequested - True if settled bets were requested.
 * It means that after login success we need to fetch settled bets immediately instead of settled bets count.
 * @property {string} dataHash - Hash object for settled bet response.
 *
 * @memberof Mojito.Services.Bets
 */
const initialState = {
    ...betsInitState,
    isRequested: false,
};

const reducers = {
    reset() {
        return { ...initialState };
    },
    resetBestState(state) {
        return { ...state, ...betsInitState };
    },
    resetRequested(state) {
        state.isRequested = false;
    },
    startFetching(state) {
        state.loadingStatus = CONTENT_STATE.PENDING;
    },
    requestSettledBets(state) {
        state.isRequested = true;
    },
    fetchSettledBetsSuccess(state, { payload }) {
        const { bets = [], dataHash } = payload;
        if (!dataHash || state.dataHash !== dataHash) {
            state.dataHash = dataHash;
            state.bets = bets;
        }
        state.loadingStatus = CONTENT_STATE.AVAILABLE;
    },
    fetchSettledBetsFailed(state) {
        state.loadingStatus = CONTENT_STATE.AVAILABLE;
    },
};

const { reducer, actions } = createSlice({
    name: STORE_KEY,
    initialState,
    reducers,
});

actions.fetchSettledBets = () => (dispatch, getState) => {
    dispatch(actions.requestSettledBets());
    const state = getState();
    if (isLoggedIn(state)) {
        if (dataRetriever.allowSettledBetsPolling()) {
            dataRetriever.refreshSettledBets(
                () => selectDataHash(getState()),
                () => dispatch(actions.startFetching())
            );
        } else if (!selectIsPending(state)) {
            const oddsFormat = selectOddsFormat(state);
            dispatch(actions.startFetching());
            dataRetriever.fetchSettledBets(oddsFormat);
        }
    }
};

actions.discardSettledBets = () => dispatch => {
    dispatch(actions.resetRequested());
};

reduxInstance.actionListener.startListening({
    actionCreator: authenticationActions.loginSuccess,
    effect: (_, listenerApi) => {
        const state = listenerApi.getState();
        const action = selectIsRequested(state)
            ? actions.fetchSettledBets()
            : actions.discardSettledBets();
        listenerApi.dispatch(action);
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: bootstrapActions.dispose,
    effect: (_, listenerApi) => {
        listenerApi.dispatch(actions.reset());
        dataRetriever.stopRefreshingSettledBets();
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: authenticationActions.disposeSession,
    effect: (_, listenerApi) => {
        listenerApi.dispatch(actions.resetBestState());
        dataRetriever.stopRefreshingSettledBets();
    },
});

/**
 * Settled bets actions.
 *
 * @class SettledBetsActions
 * @name actions
 * @memberof Mojito.Services.Bets.settledBetsSlice
 */

/**
 * Reset store.
 *
 * @function reset
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @memberof Mojito.Services.Bets.settledBetsSlice.actions
 */

/**
 * Reset store state part related to settled bets.
 *
 * @function resetBestState
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @memberof Mojito.Services.Bets.settledBetsSlice.actions
 */

/**
 * Reset "settled bets were requested" flag.
 *
 * @function resetRequested
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @memberof Mojito.Services.Bets.settledBetsSlice.actions
 */

/**
 * Start settled bets fetching.
 *
 * @function startFetching
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @memberof Mojito.Services.Bets.settledBetsSlice.actions
 */

/**
 * Request settled bets. This means user's intention to see settled bets, even without being logged in.
 * So after logging in, settled bets need to be shown immediately, not just settled bet count as usual.
 *
 * @function requestSettledBets
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @memberof Mojito.Services.Bets.settledBetsSlice.actions
 */

/**
 * Settled bets have been fetched successfully.
 *
 * @function fetchSettledBetsSuccess
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {Mojito.Services.Bets.types.SettledBetsResponse} payload - Settled bets response.
 * @memberof Mojito.Services.Bets.settledBetsSlice.actions
 */

/**
 * Fetching of settled bets has failed.
 *
 * @function fetchSettledBetsFailed
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {Mojito.Core.Services.Transactions.types.Error} payload - Error.
 * @memberof Mojito.Services.Bets.settledBetsSlice.actions
 */

/**
 * Discard settled bet information. Meaning it is not needed any more.
 * If user logged in, fetch settled bet count instead.
 *
 * @function discardSettledBets
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Thunk.
 * @memberof Mojito.Services.Bets.settledBetsSlice.actions
 */

reduxInstance.injectReducer(STORE_KEY, reducer);

export { reducer, actions, STORE_KEY, initialState };
